import { Alert, Box, IconButton, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevBackIcon } from '../../assets/icons/icons'
import { ProjectForm } from '../../components/project/ProjectForm'
import SnackBarContext from '../../context/SnackBarContext'
import { useGetEntityFormByType } from '../../hooks/entityFormHook'
import {
  useGetProject,
  useGetProjectsAll,
  useMutateCreateProject,
} from './hooks'
import moment from 'moment-timezone'
import { formatTime } from '../../lib/time'
import { debounce } from 'lodash'

export const ProjectFormNew = () => {
  const topRef = useRef<HTMLDivElement>(null); 
  const { id } = useParams()
  const navigate = useNavigate()
  const defaultProjectState = {
    projectTimeZone: moment.tz.guess(),
  }
  const [projectData, setProjectData] = React.useState<any>(defaultProjectState)
  const { data: form, isLoading: isFormLoading } = useGetEntityFormByType(
    'projectForm',
    'project'
  )
  const { data: projectRecord, isLoading: isProjectRecordLoading } =
    useGetProject('project', id)
  const { mutateAsync: mutateProjectAsync } = useMutateCreateProject('projects')
  const [errorMessage, setErrorMessage] = useState<any | undefined>('')
  const { toggleSnackbar } = useContext(SnackBarContext)
  const { data: projects } = useGetProjectsAll('projects')
  const onChangeHandler = (newFormData: any) => {
    setProjectData((prevProjectData: any) => ({
      ...prevProjectData,
      ...newFormData,
    }))
  }
  
  const scrollToTop = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const onSubmitHandler = useCallback(
    debounce(async projectData => {
    // Remove empty addresses from project locations
    projectData.locations = projectData.locations.filter(
      (obj: any) => obj.address.trim() !== ''
    )
    const existingProject = projects.find(
      (proj: { projectName: any; id: string | undefined }) =>
        proj.projectName === projectData.projectName && proj.id !== id
    )
    // Check for empty values and mark required fields with empty values
    let hasEmptyValues: any[] = []
    form.fields.forEach((f: any) => {
      if (
        typeof projectData[f.name] === 'string' &&
        projectData[f.name].trim() === ''
      ) {
        projectData[f.name] = '' // Clear empty string values
        if (f.isRequired) hasEmptyValues.push(f.name) // Mark required fields with empty values
      }
    })
    //Project already exists
    if (existingProject) {
      scrollToTop();
      setErrorMessage('Project with the same name already exists')
      return
    }
    // Validate time range
    if (!dayjs(projectData.dailyStartTime).isBefore(projectData.dailyEndTime)) {
      // Handle invalid time range error
      scrollToTop();
      setErrorMessage('Start time must be before end time.')
      return // Exit function early
    }

    // Check if project locations are empty
    if (projectData.locations.length === 0) {
      scrollToTop();
      setErrorMessage('Please provide at least one project location.')
      return // Exit function early
    }

    // Check if there are any empty required fields
    if (hasEmptyValues.length > 0) {
      scrollToTop();
      setErrorMessage('Please enter valid values for required fields.')
      return // Exit function early
    }

    // Consolidate the automatic clock out time.
    if (projectData.automaticClockOutEnabled) {
      const parsedTime = formatTime(projectData?.automaticClockOutTime)
      const timeWithTimeZone = dayjs.tz(
        parsedTime,
        'HH:mm A',
        projectData.projectTimeZone
      )
      const UTCTime = timeWithTimeZone.utc().format('HH:mm')

      projectData.automaticClockOut = {
        enabled: projectData.automaticClockOutEnabled,
        time: parsedTime,
        timeInUTC: UTCTime,
      }
    } else {
      projectData.automaticClockOut = {
        enabled: false,
        time: null,
        timeInUTC: null,
      }
    }
    try {
      let result
      let allProject
      if (id) {
        result = await mutateProjectAsync(projectData)
        toggleSnackbar('Project updated successfully', 'success')
      } else {
        allProject = await mutateProjectAsync(projectData)
        toggleSnackbar('Project created successfully', 'success')
      }

      if (result) {
        navigate(`/projects/${result._id}`)
      } else if (allProject) {
        navigate(`/projects`)
      }
    } catch (error) {
      if (id) {
        setErrorMessage(
          'An error occurred while updating the project. Please try again later.'
        )
      } else {
        setErrorMessage(
          'An error occurred while creating the project. Please try again later.'
        )
      }
    }
  }, 500), [projectData])

  const onDeleteLocation = (indexToRemove: any) => {
    setProjectData((prevProjectData: any) => ({
      ...prevProjectData,
      locations: prevProjectData.locations.filter(
        (_: any, index: any) => index !== indexToRemove
      ),
    }))
  }

  const onAddLocation = () => {
    setProjectData((prevProjectData: any) => ({
      ...prevProjectData,
      locations: [...prevProjectData.locations, { address: '' }],
    }))
  }

  useEffect(() => {
    if (errorMessage) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    if (projectRecord) {
      if (projectRecord.automaticClockOut) {
        projectRecord.automaticClockOutEnabled = projectRecord.automaticClockOut
          .enabled
          ? projectRecord.automaticClockOut.enabled
          : projectRecord.automaticClockOut.enabled
        projectRecord.automaticClockOutTime = `${dayjs().format(
          'MM/DD/YYYY'
        )} ${projectRecord.automaticClockOut.time}`
      }
      if (projectRecord.kioskMode) {
        projectRecord.kioskEnabled = projectRecord.kioskMode.enabled
      }
      setProjectData(projectRecord)
    }
  }, [projectRecord])

  return (
    <Box sx={{ padding: '0px 30px' }} ref={topRef}>
      <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
        <IconButton
          sx={{
            background: '#24212C12',
            borderRadius: 1.8,
            padding: '6px 10px',
          }}
          onClick={() => navigate(-1)}>
          <ChevBackIcon />{' '}
          <p
            style={{
              fontSize: 12,
              paddingTop: 0,
              paddingRight: 0,
              paddingBottom: 0,
              margin: 0,
              paddingLeft: 7,
            }}>
            Go Back
          </p>
        </IconButton>
      </Box>
      {form ? (
        <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
          <Typography variant='h4' gutterBottom>
            {id ? 'Edit' : 'Create'} {form.formName}{' '}
            {id && `- ${projectData?.projectName}`}
          </Typography>
          <Box sx={{ marginTop: 6 }}>
            {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
            {form ? (
              <ProjectForm
                fields={form.fields}
                options={[]}
                onFormDataChange={onChangeHandler}
                onSubmit={onSubmitHandler}
                record={projectRecord ? projectRecord : projectData}
                onDeleteLocation={onDeleteLocation}
                onAddLocation={onAddLocation}
              />
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}
